import { useRef, useState } from "react";
import ReCaptchaInput, { ReCaptchaHandle } from "@/common/input/ReCaptchaInput";
import { motion } from "framer-motion";
import { Copy, Check } from "lucide-react";
import { toastErrorMessage, toastSuccessMessage } from "@/utils/helper/myToast";
import { SideBarContentHeader } from "@/common/ui/SideBar";
import { H3, LightText } from "@/common/ui/Text";
import { Button } from "@/components/ui/button";
import { GoogleOAuthProvider } from "@react-oauth/google";
import GoogleSignInButton from "@/common/ui/GoogleSignInButton";
import { GOOGLE_CLIENT_ID } from "@/utils/constant/globalEnv.constant";
import { Col, Row } from "@/common/css";

const QATestingView = () => {
  const reCaptchaRef = useRef<ReCaptchaHandle>(null);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [googleCode, setGoogleCode] = useState("");
  const [googleCredential, setGoogleCredential] = useState("");

  const handleGenerateReCaptcha = async () => {
    if (reCaptchaRef.current) {
      const token = await reCaptchaRef.current.getRecaptchaToken();
      if (token) {
        setRecaptchaToken(token);
        handleCopy(token);
      }
    }
  };

  const handleCopy = (text: string) => {
    if (document.hasFocus()) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setIsCopied(true);
          toastSuccessMessage("Token copied to clipboard!");
          setTimeout(() => setIsCopied(false), 2000);
        })
        .catch((err) => {
          toastErrorMessage("Failed to copy token.");
          console.error("Error copying to clipboard: ", err);
        });
    } else {
      toastErrorMessage("Document is not focused. Please try again.");
    }
  };

  const handleOnClickGoogleBtn = async (data: any) => {
    let token = null;
    if (data?.credential) {
      console.log("One Tab Google Sign");
      token = data?.credential;
      setGoogleCredential(token);
    } else if (data?.code) {
      console.log("Google Sign-in");
      token = data?.code;
      setGoogleCode(token);
    }
    if (token) {
      if (document.hasFocus()) {
        // Copy the code to the clipboard
        navigator.clipboard
          .writeText(token)
          .then(() => {
            // Show success toast
            toastSuccessMessage("Code copied to clipboard!");
          })
          .catch((err) => {
            // Show error toast
            toastErrorMessage("Failed to copy code.");
            console.error("Error copying to clipboard: ", err);
          });
      } else {
        toastErrorMessage("Document is not focused. Please try again.");
      }
    }
  };

  return (
    <div className="px-4">
      <SideBarContentHeader heading="Token For API Testing" />

      <Col className="w-full py-4">
        <H3 text="ReCAPTCHA Token" />
        <Row className=" gap-x-2 pt-5">
          <Button variant="outline" className="text-13 text-text-primary " onClick={handleGenerateReCaptcha}>
            Generate
          </Button>
          <LightText text={"expires in 2 min"} />
        </Row>
      </Col>
      {recaptchaToken && (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mt-4 relative">
          <input
            type="text"
            value={recaptchaToken}
            readOnly
            className="w-full p-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} className=" absolute right-2 top-3" onClick={() => handleCopy(recaptchaToken)}>
            {isCopied ? <Check className="text-green-500" size={20} /> : <Copy className="text-gray-500" size={20} />}
          </motion.button>
        </motion.div>
      )}
      {/* Invisible ReCaptcha */}
      <ReCaptchaInput ref={reCaptchaRef} />
      {/* Google login */}
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <div className=" max-w-[300px]">
          <GoogleSignInButton handleSignIn={handleOnClickGoogleBtn} btnText="Login with Google" isLoading={false} />
        </div>
      </GoogleOAuthProvider>
      <Col className="w-full py-4">
        <H3 text="Google Code" />
        <Row className=" gap-x-2 pt-5">
          <LightText text={"expires in 1 min"} />
        </Row>
      </Col>
      {
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mt-4 relative">
          <input
            type="text"
            value={googleCode}
            readOnly
            placeholder="empty"
            className="w-full p-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} className=" absolute right-2 top-3" onClick={() => handleCopy(googleCode)}>
            {isCopied ? <Check className="text-green-500" size={20} /> : <Copy className="text-gray-500" size={20} />}
          </motion.button>
        </motion.div>
      }
      <Col className="w-full py-4">
        <H3 text="Google Credential ( One Tap )" />
        <Row className=" gap-x-2 pt-5">
          <LightText text={"expires in 1 min"} />
        </Row>
      </Col>
      {
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mt-4 relative">
          <input
            type="text"
            value={googleCredential}
            placeholder="empty"
            readOnly
            className="w-full p-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} className=" absolute right-2 top-3" onClick={() => handleCopy(googleCredential)}>
            {isCopied ? <Check className="text-green-500" size={20} /> : <Copy className="text-gray-500" size={20} />}
          </motion.button>
        </motion.div>
      }
    </div>
  );
};

export default QATestingView;
